import type { FilterOption } from '@/components/calendar/CascadingFilter';
import type { CalendarEvent, CountryCode, LensId, EntityOption, PillNavLink } from './types';
import { uiFb } from './i18n-helpers';
import { getCountryLensSlugForId } from './slug-country-lens';
import { getCountrySlugFromCode } from './long-weekends';
import { getCities } from './data-source';
import { getCitySlugForId } from './slug-city';
import { getSlugForCountry } from './slug-country';

// =============================================================================
// Filter config: per-page-type filter options + predicate factories
// =============================================================================

export interface FilterConfig {
  navLinks: PillNavLink[];
  filterOptions: FilterOption[];
  /** Given a filter ID, returns a predicate to filter events */
  predicate: (filterId: string) => (event: CalendarEvent) => boolean;
}

// -----------------------------------------------------------------------------
// Country pages
// -----------------------------------------------------------------------------

export function getCountryFilterConfig(
  lang: string,
  countryCode: CountryCode,
): FilterConfig {
  const filterOptions: FilterOption[] = [
    { id: 'all', label: uiFb('ui.filter.all', 'All', lang), icon: '📋' },
    { id: 'public_holiday', label: uiFb('ui.filter.public_holidays', 'Public Holidays', lang), icon: '🏛️' },
    { id: 'observance', label: uiFb('ui.filter.observances', 'Observances', lang), icon: '🕯️' },
  ];

  // Navigation pills to dedicated lens pages
  const navLinks: PillNavLink[] = [];

  const religionSlug = getCountryLensSlugForId(countryCode, 'religion', lang);
  if (religionSlug) {
    navLinks.push({ label: uiFb('ui.lens_religion', 'Religion', lang), href: '/' + religionSlug, icon: '🙏' });
  }

  const natureSlug = getCountryLensSlugForId(countryCode, 'nature', lang);
  if (natureSlug) {
    navLinks.push({ label: uiFb('ui.lens_nature', 'Nature', lang), href: '/' + natureSlug, icon: '🌿' });
  }

  const lwSlug = getCountrySlugFromCode(countryCode);
  if (lwSlug) {
    navLinks.push({ label: uiFb('ui.lens_long_weekend', 'Long Weekend', lang), href: '/' + lwSlug, icon: '🏖️' });
  }

  return {
    navLinks,
    filterOptions,
    predicate: (filterId: string) => {
      if (filterId === 'all') return () => true;
      if (filterId === 'public_holiday') return (e) => e.type === 'public_holiday' || e.type === 'national_day';
      if (filterId === 'observance') return (e) => e.type === 'observance';
      return () => true;
    },
  };
}

// -----------------------------------------------------------------------------
// City pages
// -----------------------------------------------------------------------------

export function getCityFilterConfig(lang: string, cityId?: string): FilterConfig {
  const navLinks: PillNavLink[] = [];
  if (cityId) {
    const cities = getCities();
    const currentCity = cities.find(c => c.id === cityId);
    if (currentCity) {
      for (const city of cities) {
        if (city.id === cityId || city.country !== currentCity.country) continue;
        const slug = getCitySlugForId(city.id, lang) || getCitySlugForId(city.id, 'en');
        if (!slug) continue;
        navLinks.push({ label: city.name, href: '/' + slug, icon: city.icon });
      }
    }
  }

  return {
    navLinks,
    filterOptions: [
      { id: 'all', label: uiFb('ui.filter.all', 'All', lang), icon: '📋' },
      { id: 'cultural_festival', label: uiFb('ui.filter.cultural_festivals', 'Cultural Festivals', lang), icon: '🎭' },
      { id: 'local_event', label: uiFb('ui.filter.local_events', 'Local Events', lang), icon: '📍' },
      { id: 'public_holiday', label: uiFb('ui.filter.public_holidays', 'Public Holidays', lang), icon: '🏛️' },
    ],
    predicate: (filterId: string) => {
      if (filterId === 'all') return () => true;
      if (filterId === 'public_holiday') return (e) => e.type === 'public_holiday' || e.type === 'national_day';
      return (e) => e.type === filterId;
    },
  };
}

// -----------------------------------------------------------------------------
// Academic / university pages
// -----------------------------------------------------------------------------

export function getAcademicFilterConfig(lang: string): FilterConfig {
  return {
    navLinks: [],
    filterOptions: [
      { id: 'all', label: uiFb('ui.filter.all', 'All', lang), icon: '📋' },
      { id: 'new_student', label: uiFb('ui.filter.new_student', 'New Student Admission', lang), icon: '🎒', children: [
        { id: 'new_student', label: uiFb('ui.filter.all_new_students', 'All New Students', lang), icon: '🎒' },
        { id: 'new_student_snbp', label: uiFb('ui.filter.snbp', 'SNBP', lang), icon: '🏅' },
        { id: 'new_student_snbt', label: uiFb('ui.filter.snbt', 'SNBT', lang), icon: '📝' },
        { id: 'new_student_mandiri', label: uiFb('ui.filter.mandiri', 'Mandiri', lang), icon: '🏫' },
      ]},
      { id: 'final_year', label: uiFb('ui.filter.final_year', 'Final Year', lang), icon: '🎓' },
      { id: 'odd_semester', label: uiFb('ui.filter.odd_semester', 'Odd Semester', lang), icon: '📘' },
      { id: 'even_semester', label: uiFb('ui.filter.even_semester', 'Even Semester', lang), icon: '📗' },
    ],
    predicate: (filterId: string) => {
      if (filterId === 'all') return () => true;
      if (filterId === 'new_student') return (e) =>
        e.type === 'school_term_start' || e.type === 'enrollment_deadline' || e.type === 'school_holiday';
      if (filterId === 'new_student_snbp') return (e) =>
        e.type === 'school_term_start' || e.type === 'school_holiday' ||
        (e.type === 'enrollment_deadline' && e.id.includes('-snbp-'));
      if (filterId === 'new_student_snbt') return (e) =>
        e.type === 'school_term_start' || e.type === 'school_holiday' ||
        (e.type === 'enrollment_deadline' && e.id.includes('-snbt-'));
      if (filterId === 'new_student_mandiri') return (e) =>
        e.type === 'school_term_start' || e.type === 'school_holiday' ||
        (e.type === 'enrollment_deadline' && e.id.includes('-mandiri-'));
      if (filterId === 'final_year') return (e) =>
        e.type === 'exam_period' || e.type === 'school_term_end' || e.type === 'observance';
      if (filterId === 'odd_semester') return (e) => e.id.includes('-odd-');
      if (filterId === 'even_semester') return (e) => e.id.includes('-even-');
      return () => true;
    },
  };
}

// -----------------------------------------------------------------------------
// Country-lens pages (religion/nature for a specific country)
// These use LensSwitcher-style navigation pills with dropdowns
// -----------------------------------------------------------------------------

export function getCountryLensFilterConfig(
  lang: string,
  countryCode: CountryCode,
  activeLens: LensId,
  entities: Record<LensId, EntityOption[]>,
): FilterConfig {
  const navLinks: PillNavLink[] = [];
  const filterOptions: FilterOption[] = [];

  // Add parent country page link
  const countrySlug = getSlugForCountry(countryCode, lang) || getSlugForCountry(countryCode, 'en');
  if (countrySlug) {
    navLinks.push({
      label: uiFb('ui.lens_all_holidays', 'All Holidays', lang),
      href: '/' + countrySlug,
      icon: '📋',
    });
  }

  // Build navigation pills and filter options for each lens
  const lensOrder: LensId[] = ['religion', 'nature', 'long-weekend'];
  for (const lensId of lensOrder) {
    const ents = entities[lensId] ?? [];
    // Hide long-weekend without country context; hide nature if no events
    if (lensId === 'long-weekend' && !countryCode) continue;
    if (lensId === 'nature' && ents.length === 0) continue;

    // Check if a dedicated per-country lens page exists
    const hasCountryLensPage =
      (lensId === 'religion' && getCountryLensSlugForId(countryCode, 'religion', lang) !== '') ||
      (lensId === 'nature' && getCountryLensSlugForId(countryCode, 'nature', lang) !== '') ||
      (lensId === 'long-weekend' && (getCountrySlugFromCode(countryCode) ?? '') !== '');

    const lensLabel = uiFb(`ui.lens_${lensId.replace('-', '_')}`, lensId, lang);
    const lensIcon = lensId === 'religion' ? '🙏' : lensId === 'nature' ? '🌿' : '🏖️';

    if (hasCountryLensPage && lensId !== activeLens) {
      // Navigation pill to dedicated page
      let href = '';
      if (lensId === 'religion') href = '/' + getCountryLensSlugForId(countryCode, 'religion', lang);
      else if (lensId === 'nature') href = '/' + getCountryLensSlugForId(countryCode, 'nature', lang);
      else if (lensId === 'long-weekend') href = '/' + (getCountrySlugFromCode(countryCode) ?? '');

      if (href && href !== '/') {
        navLinks.push({ label: lensLabel, href, icon: lensIcon });
      }
    } else if (ents.length > 1 && lensId === activeLens) {
      // Active lens with dropdown sub-entities
      const groupLabel =
        lensId === 'religion' ? uiFb('ui.filter.by_faith', 'By Faith', lang)
        : lensId === 'nature' ? uiFb('ui.filter.by_season', 'By Season', lang)
        : lensId === 'long-weekend' ? uiFb('ui.filter.by_type', 'By Type', lang)
        : undefined;

      const children: FilterOption[] = ents.map(opt => ({
        id: opt.id,
        label: opt.label,
        icon: opt.icon,
      }));

      filterOptions.push({ id: lensId, label: lensLabel, icon: lensIcon, children, groupLabel });
    } else if (!hasCountryLensPage) {
      // Simple filter pill (no dedicated page, no dropdown)
      filterOptions.push({ id: lensId, label: lensLabel, icon: lensIcon });
    }
  }

  return {
    navLinks,
    filterOptions,
    predicate: () => () => true, // Country-lens pages use store-based filtering, not predicate
  };
}

// -----------------------------------------------------------------------------
// Resolver: pick the right config for a given page context
// -----------------------------------------------------------------------------

export function getFilterConfigForPage(
  lens: LensId,
  lang: string,
  countryCode?: string,
  entities?: Record<LensId, EntityOption[]>,
  year?: number,
  entity?: string,
): FilterConfig | null {
  if (lens === 'city') return getCityFilterConfig(lang, entity);
  if (lens === 'academic') return getAcademicFilterConfig(lang);

  if (lens === 'country' && countryCode) {
    return getCountryFilterConfig(lang, countryCode as CountryCode);
  }

  // Country-scoped religion/nature/long-weekend pages
  if ((lens === 'religion' || lens === 'nature' || lens === 'long-weekend') && countryCode && entities) {
    return getCountryLensFilterConfig(lang, countryCode as CountryCode, lens, entities);
  }

  return null;
}
